import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ReferenceArea, XAxis, YAxis } from 'recharts';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import type { CohortRow, CohortTable, ImpactMetric, MetricPopulation } from '@/services/changelog';
import { ACCOUNTS, countLabel } from './changelog-labels';
import CohortMembersSheet, { type CohortSelection } from './CohortMembersSheet';

interface Props {
  cohorts: CohortTable;
  /** ISO date of the change the page is about; the cohorts split on it. */
  changeDate: string;
  /** Entry slug, so a clicked bar can fetch the accounts behind it. */
  slug: string;
  /** Which chart these bars belong to: decides the axis unit and, more
   *  importantly, which population the drill-down lists. */
  metricKey: string;
  unit: ImpactMetric['unit'];
  /** What the counts are: accounts, or trials for Trial to paid. */
  population?: MetricPopulation;
  /** Chart heading, which differs by unit ("converted" vs "were worth"). */
  caption: string;
}

const shortDate = (d: Date) =>
  d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

const DAY_MS = 86_400_000;

interface Bucket {
  fromDay: number;
  /** Null is the uncapped "so far" bucket: everyone eligible, still growing. */
  toDay: number | null;
}

const sameBucket = (
  a: { fromDay: number; toDay: number | null },
  b: { fromDay: number; toDay: number | null },
) => a.fromDay === b.fromDay && a.toDay === b.toDay;

/**
 * Buckets in the order the API sends them (0 to 7, 7 to 30, 30 to 60, then
 * "so far"), deduplicated across cohorts.
 */
const bucketsOf = (rows: CohortRow[]): Bucket[] => {
  const seen: Bucket[] = [];
  for (const r of rows) if (!seen.some((b) => sameBucket(b, r))) seen.push({ fromDay: r.fromDay, toDay: r.toDay });
  return seen;
};

const bucketLabel = (b: Bucket) =>
  b.toDay === null ? 'So far' : `${b.fromDay} to ${b.toDay} days`;

/** A cell is plottable only when someone in the cohort has reached the bucket's end day. */
const plottable = (row: CohortRow | undefined): number | null =>
  row && row.eligible > 0 && row.value !== null ? row.value : null;

type CohortKey = 'before' | 'since';

type Lift = { text: string; tone: 'up' | 'down' | 'flat' };

/**
 * Since minus before: the number the page exists to show, so it should not
 * have to be worked out in the reader's head from two bar labels. Money
 * carries its relative change too ("+$10.16 (+38%)"). A rate shows the gap
 * between the two bars ("+8.5%" for 24.0% -> 32.5%), NOT a relative rise:
 * "+35%" of a percentage reads as nonsense, and "pts" was jargon (user,
 * 2026-09-07). Null when either bar is missing, which the legend explains.
 */
const liftOf = (before: number | null, since: number | null, money: boolean): Lift | null => {
  if (before === null || since === null) return null;
  const diff = since - before;
  const tone: Lift['tone'] = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
  const sign = diff > 0 ? '+' : diff < 0 ? '-' : '';
  const abs = Math.abs(diff);
  if (money) {
    const rel = before > 0 ? ` (${sign}${Math.round((abs / before) * 100)}%)` : '';
    return { text: `${sign}$${abs.toFixed(2)}${rel}`, tone };
  }
  return { text: `${sign}${abs.toFixed(1)}%`, tone };
};

const LIFT_CLASS: Record<Lift['tone'], string> = {
  up: 'fill-emerald-600 dark:fill-emerald-400',
  down: 'fill-red-600 dark:fill-red-400',
  flat: 'fill-muted-foreground',
};

/** Bucket name on the first line, its lift on the second. */
const LiftTick = ({
  x,
  y,
  payload,
  lifts,
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
  lifts: Map<string, Lift | null>;
}) => {
  const lift = payload ? lifts.get(payload.value) : null;
  return (
    <g transform={`translate(${x ?? 0},${y ?? 0})`}>
      <text textAnchor="middle" dy={12} fontSize={12} className="fill-muted-foreground">
        {payload?.value}
      </text>
      {lift && (
        <text textAnchor="middle" dy={28} fontSize={11} className={`font-medium tabular-nums ${LIFT_CLASS[lift.tone]}`}>
          {lift.text}
        </text>
      )}
    </g>
  );
};

/**
 * The "and how long until they convert" view the line chart cannot give
 * without waiting that long. Before and since the change, side by side, one
 * group per bucket of days after signup; the total last. A bar reads over the
 * accounts old enough to have reached the bucket's end day, so a young cohort
 * shows "31% of 29" rather than a rate that collapses; a missing bar means no
 * one is old enough yet.
 *
 * Clicking a bar opens the accounts behind it. A bar with nothing to plot is
 * not clickable: there is no list to show.
 */
const CohortBreakdown = ({ cohorts, changeDate, slug, metricKey, unit, population = ACCOUNTS, caption }: Props) => {
  // The cohort-members route lists the accounts behind signup cohorts only; a
  // metric it can't list draws bars that don't open.
  const drillDown = cohorts.drillDown ?? true;
  const [selection, setSelection] = useState<CohortSelection | null>(null);
  const buckets = bucketsOf(cohorts.rows);
  const rowFor = (cohort: CohortKey, b: Bucket) =>
    cohorts.rows.find((r) => r.cohort === cohort && sameBucket(r, b));

  const data = buckets.map((b) => ({
    bucket: bucketLabel(b),
    toDay: b.toDay,
    before: plottable(rowFor('before', b)),
    since: plottable(rowFor('since', b)),
    rows: { before: rowFor('before', b), since: rowFor('since', b) },
  }));

  const signups = (cohort: CohortKey) => cohorts.rows.find((r) => r.cohort === cohort)?.signups ?? 0;
  const lifts = new Map(data.map((d) => [d.bucket, liftOf(d.before, d.since, unit === 'currency')]));

  const changeAt = new Date(`${changeDate}T00:00:00Z`);
  const beforeStart = new Date(changeAt.getTime() - cohorts.spanDays * DAY_MS);
  const dayBefore = new Date(changeAt.getTime() - DAY_MS);

  const config: ChartConfig = {
    before: { label: `${cohorts.spanDays} days before the change`, color: 'var(--muted-foreground)' },
    since: { label: 'Since the change', color: 'var(--chart-3)' },
  };

  const isMoney = unit === 'currency';
  /** Bar labels and axis ticks. Money keeps cents below $1000, same as the line. */
  const amount = (v: unknown) => {
    if (typeof v !== 'number') return '';
    if (!isMoney) return `${v.toFixed(1)}%`;
    return v >= 1000 ? `$${Math.round(v).toLocaleString()}` : `$${v.toFixed(2)}`;
  };

  /**
   * Two lines above every bar: the comparable number big, and the exact
   * figures behind it small ("$6,670 from 249", "37 of 249"), so the
   * division is visible without a hover.
   */
  const barLabel =
    (cohort: CohortKey, cls: string) =>
    (props: {
      x?: number | string;
      y?: number | string;
      width?: number | string;
      value?: number | string | null;
      index?: number;
    }) => {
      const { x = 0, y = 0, width = 0, value, index } = props;
      if (typeof value !== 'number') return null;
      const row = index === undefined ? undefined : data[index]?.rows[cohort];
      const cx = Number(x) + Number(width) / 2;
      const exact = !row
        ? ''
        : isMoney
          ? `$${Math.round(row.amount ?? 0).toLocaleString()} from ${row.eligible}`
          : `${row.converted} of ${row.eligible}`;
      return (
        <g>
          <text x={cx} y={Number(y) - 15} textAnchor="middle" fontSize={10} className={cls}>
            {amount(value)}
          </text>
          {exact && (
            <text x={cx} y={Number(y) - 4} textAnchor="middle" fontSize={9} className="fill-muted-foreground">
              {exact}
            </text>
          )}
        </g>
      );
    };

  /**
   * Turns a clicked bar into the sheet's selection. Returns null for a bar
   * with nothing plotted, so a click on empty space is a no-op rather than a
   * panel of nothing.
   */
  const selectionFor = (cohort: CohortKey, b: Bucket): CohortSelection | null => {
    const row = rowFor(cohort, b);
    if (!drillDown || !row || plottable(row) === null || row.converted === 0) return null;
    return {
      cohort,
      fromDay: b.fromDay,
      toDay: b.toDay,
      bucketLabel: bucketLabel(b),
      cohortLabel: String(config[cohort].label),
      converted: row.converted,
      eligible: row.eligible,
      value: row.value,
      amount: row.amount,
      unit,
    };
  };

  const isSelected = (cohort: CohortKey, b: Bucket) =>
    selection !== null && selection.cohort === cohort && sameBucket(selection, b);

  /** Recharts hands the clicked datum's index; the bar knows its own series. */
  const onBarClick = (cohort: CohortKey) => (_datum: unknown, index: number) => {
    const b = buckets[index];
    if (b) setSelection(selectionFor(cohort, b));
  };

  return (
    <div className="mt-5 border-t pt-4">
      <p className="text-xs font-medium text-muted-foreground">{caption}</p>

      <ChartContainer config={config} className="h-[232px] w-full mt-1">
        <BarChart
          data={data}
          accessibilityLayer
          margin={{ left: 0, right: 12, top: 30, bottom: 0 }}
          barCategoryGap="28%"
          barGap={4}
        >
          <CartesianGrid vertical={false} strokeDasharray="0" />
          {/* "So far" is a different kind of number (uncapped, still growing),
              so its group sits on its own shaded band. */}
          {data.some((d) => d.toDay === null) && (
            <ReferenceArea
              x1={bucketLabel({ fromDay: 0, toDay: null })}
              x2={bucketLabel({ fromDay: 0, toDay: null })}
              fill="var(--muted)"
              fillOpacity={0.5}
            />
          )}
          <XAxis
            dataKey="bucket"
            axisLine={false}
            tickLine={false}
            tickMargin={8}
            height={44}
            tick={(props: { x?: number; y?: number; payload?: { value: string } }) => (
              <LiftTick {...props} lifts={lifts} />
            )}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tickMargin={8}
            width={48}
            tickFormatter={(v: number) => (isMoney ? `$${v}` : `${v}%`)}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(label) => String(label)}
                formatter={(_value, name, item: any) => {
                  const key = String(name) as CohortKey;
                  const row: CohortRow | undefined = item?.payload?.rows?.[key];
                  const toDay: number | null = item?.payload?.toDay ?? null;
                  return (
                    <>
                      <div
                        className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                        style={{ backgroundColor: config[key].color }}
                      />
                      <div className="flex flex-1 justify-between gap-4 leading-none items-center">
                        <span className="text-muted-foreground">{config[key].label}</span>
                        {plottable(row) === null ? (
                          <span className="text-muted-foreground">
                            {toDay === null
                              ? `no ${population.many} in this cohort yet`
                              : `no ${population.one} is ${toDay} days old yet`}
                          </span>
                        ) : (
                          <span className="text-foreground font-mono font-medium tabular-nums">
                            {amount(row!.value)}
                            <span className="ml-1.5 font-sans font-normal text-muted-foreground">
                              {isMoney && row!.amount !== undefined
                                ? `$${Math.round(row!.amount).toLocaleString()} from ${countLabel(row!.eligible, population)}`
                                : `${row!.converted} of ${countLabel(row!.eligible, population)}`}
                            </span>
                          </span>
                        )}
                      </div>
                    </>
                  );
                }}
              />
            }
          />
          <Bar
            dataKey="before"
            fill="var(--muted-foreground)"
            fillOpacity={0.45}
            radius={[3, 3, 0, 0]}
            onClick={onBarClick('before')}
          >
            {buckets.map((b) => (
              <Cell
                key={`before-${bucketLabel(b)}`}
                cursor={selectionFor('before', b) ? 'pointer' : 'default'}
                fillOpacity={isSelected('before', b) ? 0.8 : 0.45}
                stroke={isSelected('before', b) ? 'var(--foreground)' : undefined}
                strokeWidth={isSelected('before', b) ? 1.5 : 0}
              />
            ))}
            <LabelList dataKey="before" content={barLabel('before', 'fill-muted-foreground')} />
          </Bar>
          <Bar
            dataKey="since"
            fill="var(--chart-3)"
            radius={[3, 3, 0, 0]}
            onClick={onBarClick('since')}
          >
            {buckets.map((b) => (
              <Cell
                key={`since-${bucketLabel(b)}`}
                cursor={selectionFor('since', b) ? 'pointer' : 'default'}
                stroke={isSelected('since', b) ? 'var(--foreground)' : undefined}
                strokeWidth={isSelected('since', b) ? 1.5 : 0}
              />
            ))}
            <LabelList dataKey="since" content={barLabel('since', 'fill-foreground')} />
          </Bar>
        </BarChart>
      </ChartContainer>

      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-2.5 w-2.5 rounded-[2px] bg-muted-foreground/45" />
          {cohorts.spanDays} days before the change, {shortDate(beforeStart)} to {shortDate(dayBefore)} ({countLabel(signups('before'), population)})
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: 'var(--chart-3)' }} />
          Since the change, {shortDate(changeAt)} to today ({countLabel(signups('since'), population)})
        </span>
        <span>A missing bar means no one in that cohort is old enough for the bucket yet.</span>
        {drillDown && <span>Click a bar to see the accounts behind it.</span>}
      </div>

      <CohortMembersSheet
        slug={slug}
        metricKey={metricKey}
        selection={selection}
        onClose={() => setSelection(null)}
      />
    </div>
  );
};

export default CohortBreakdown;
